/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import { Column, GenericTable } from "../../components/GenericTable";
import { useMatchmakingService } from "../../services/backend/MatchmakingService";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import { AppContext } from "../../utils/AppContext";

interface MatchType {
  id: string;
  queueConfigurationId: string;
  ticketIds: string[];
  playerIds: string[];
  totalPlayers: string;
  status: string;
  tfid: string;
  matchmakingCategory: string;
  createdAt: string;
}

const MatchViewer = () => {
  const context = useContext(AppContext);

  const columns: Column<MatchType>[] = [
    { key: "id", label: "Match ID", type: "string" },
    { key: "queueConfigurationId", label: "Queue Configuration ID", type: "string" },
    {
      key: "ticketIds",
      label: "Ticket IDs",
      type: "string",
      render: (value: string | string[]) => (Array.isArray(value) ? value.join(", ") : value),
    },
    {
      key: "playerIds",
      label: "Player IDs",
      type: "string",
      render: (value: string | string[]) => (Array.isArray(value) ? value.join(", ") : value),
    },
    { key: "totalPlayers", label: "Total Players", type: "string" },
    { key: "status", label: "Status", type: "string" },
    { key: "tfid", label: "TFID (Neighborhood, Expedition...)", type: "string" },
    { key: "matchmakingCategory", label: "Category", type: "string" },
    {
      key: "createdAt",
      label: "Created At",
      type: "string",
      enabledSort: "desc",
      render: (value: string | string[]) => (!Array.isArray(value) && value ? new Date(value).toISOString() : "N/A"),
    },
  ];

  const matchmakingService = useMatchmakingService();

  const [matchList, setMatchList] = useState<MatchType[]>([]);

  // Alerts
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const fetchMatchList = async () => {
    try {
      const { matches } = await matchmakingService.listMatches();
      setMatchList(
        matches.map((match: MatchType, index: number) => ({
          ...match,
          id: match.id || String(index),
        })),
      );
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of matches.");
      setAlertSeverity("error");
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  useEffect(() => {
    context?.setTitle("Match Viewer");
    context?.setTargetAPI("environment");
    if (context?.selectedEnvironment) {
      fetchMatchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.selectedEnvironment]);

  return (
    <div>
      <GenericTable
        rows={matchList}
        columns={columns}
        enablePagination={true}
        enableSorting={true}
        onRefresh={fetchMatchList}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MatchViewer;
